/** @jsx jsx */
import { Box, Flex, Label, Link, Text } from '@bottlebooks/gatsby-theme-base';
import { Plural, Trans } from '@lingui/macro';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import { jsx } from 'theme-ui';
import BrandLogo from '../Brand/BrandLogo';
import useExhibitorSearch from '../ExhibitorsPage/useExhibitorSearch';
import FilterField from '../Filters/FilterField';
import Mark from '../Mark';
import ProductImage from '../Product/ProductImage';
import useProductSearch from '../ProductsPage/useProductSearch';

export default function SiteSearch({
  initialTerm = '',
  variant,
  locale,
  getExhibitorLink,
  getProductLink,
  paths,
  ...rest
}) {
  const [searchTerm, setSearchTerm] = React.useState(initialTerm);
  const {
    allExhibitor: { nodes: allExhibitors },
    allProduct: { nodes: allProducers },
  } = useStaticQuery(QUERY);
  const { exhibitors, products } = React.useMemo(
    () => ({
      exhibitors: allExhibitors.filter(item => item.locale === locale),
      products: allProducers.filter(item => item.locale === locale),
    }),
    [allExhibitors, allProducers, locale]
  );
  const filteredExhibitors = useExhibitorSearch(exhibitors, searchTerm);
  const filteredProducts = useProductSearch(products, searchTerm);
  return (
    <Box>
      <FilterField
        variant={variant}
        value={searchTerm}
        onChange={setSearchTerm}
        sx={{ borderRadius: 'default' }}
        {...rest}
      />
      {searchTerm && (
        <SearchResults
          searchTerm={searchTerm}
          exhibitors={filteredExhibitors}
          products={filteredProducts}
          getExhibitorLink={getExhibitorLink}
          getProductLink={getProductLink}
          paths={paths}
          sx={{ marginTop: 2, zIndex: 'modal' }}
        />
      )}
    </Box>
  );
}

function SearchResults({
  exhibitors,
  products,
  getExhibitorLink,
  getProductLink,
  searchTerm,
  paths,
  ...rest
}) {
  const productCount = products?.length || 0;
  const exhibitorCount = exhibitors?.length || 0;
  return (
    <Box
      sx={{
        position: 'absolute',
        backgroundColor: 'white',
        borderRadius: 'default',
        boxShadow: 'overlay',
        left: 0,
        right: 0,
        paddingY: 4,
      }}
      {...rest}
    >
      {exhibitors?.slice(0, 6).map(exhibitor => (
        <ExhibitorSearchResult
          key={exhibitor.slug}
          to={getExhibitorLink(exhibitor)}
          exhibitor={exhibitor}
          searchTerm={searchTerm}
        />
      ))}
      {exhibitorCount < 6 &&
        products
          ?.slice(0, 6 - exhibitorCount)
          .map(product => (
            <ProductSearchResult
              key={product.slug}
              to={getProductLink(product)}
              product={product}
              searchTerm={searchTerm}
            />
          ))}
      {(exhibitorCount > 6 || productCount > 6) && (
        <Box sx={{ paddingTop: 4 }}>
          <Label sx={{ color: 'primary', marginBottom: 3, paddingX: 4 }}>
            <Trans>Explore</Trans>
          </Label>
          {exhibitorCount > 6 && (
            <SearchResultLink
              to={`${paths.exhibitors}#searchTerm=${encodeURIComponent(
                searchTerm
              )}`}
              sx={{ paddingY: 1 }}
            >
              <Text sx={{ fontWeight: 'body' }}>
                <Plural
                  value={exhibitorCount}
                  one={<Trans>1 exhibitor with "{searchTerm}"</Trans>}
                  other={<Trans># exhibitors with "{searchTerm}"</Trans>}
                />
              </Text>
            </SearchResultLink>
          )}
          {productCount > 6 && (
            <SearchResultLink
              variant="text"
              to={`${paths.products}#searchTerm=${encodeURIComponent(
                searchTerm
              )}`}
              sx={{ paddingY: 1 }}
            >
              <Text sx={{ fontWeight: 'body' }}>
                <Plural
                  value={productCount}
                  one={<Trans>1 product with "{searchTerm}"</Trans>}
                  other={<Trans># products with "{searchTerm}"</Trans>}
                />
              </Text>
            </SearchResultLink>
          )}
        </Box>
      )}
      {exhibitorCount === 0 && productCount === 0 && (
        <Text sx={{ paddingX: 4 }}>
          Nothing found. Try another search term.
        </Text>
      )}
    </Box>
  );
}

function SearchResultLink({ to, children, ...rest }) {
  return (
    <Link
      variant="text"
      to={to}
      sx={{
        display: 'block',
        paddingY: 2,
        paddingX: 4,
        ':hover': { backgroundColor: 'light' },
      }}
      {...rest}
    >
      {children}
    </Link>
  );
}

function LargeText(props) {
  return <Text variant="large" {...props} />;
}

function ExhibitorSearchResult({ exhibitor, searchTerm, to }) {
  return (
    <SearchResultLink to={to}>
      <Flex gap={2} align="center">
        <BrandLogo brand={exhibitor} size="smaller" />
        <Box>
          <Mark
            text={exhibitor.name}
            term={searchTerm}
            render={match => <span sx={{ fontWeight: 'body' }}>{match}</span>}
            Wrapper={LargeText}
            append={term => (
              <Text variant="small" sx={{ fontWeight: 'light' }}>
                <Mark
                  text={[
                    exhibitor.countryName,
                    exhibitor.region,
                    exhibitor.city,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                  term={term}
                  render={match => (
                    <span sx={{ fontWeight: 'body' }}>{match}</span>
                  )}
                />
              </Text>
            )}
          />
        </Box>
      </Flex>
    </SearchResultLink>
  );
}

function SmallLabel(props) {
  return <Label variant="small" sx={{ fontWeight: 'light' }} {...props} />;
}

function ProductSearchResult({ product, searchTerm, to }) {
  const producerName = product.producer?.name || product.exhibitor?.name;
  const productName = `${product.shortName || ''} ${product.vintage || ''}`;
  return (
    <SearchResultLink to={to}>
      <Flex gap={2} align="center">
        <ProductImage size="smaller" product={product} />
        <Box>
          {/* Try to find the search term in the product name first. */}
          <Mark
            text={productName}
            term={searchTerm}
            render={match => <span sx={{ fontWeight: 'body' }}>{match}</span>}
            Wrapper={({ children }) => (
              <React.Fragment>
                <SmallLabel>{producerName}</SmallLabel>
                <Text sx={{ fontWeight: 'light' }}>{children}</Text>
              </React.Fragment>
            )}
            // As a fallback, find the search term in the producer name and the rest in the product name.
            fallback={() => (
              <Mark
                text={producerName}
                term={searchTerm}
                render={match => (
                  <span sx={{ fontWeight: 'body' }}>{match}</span>
                )}
                Wrapper={SmallLabel}
                // appends the product name, searching for the remaining term there.
                append={remainingTerm => (
                  <Text sx={{ fontWeight: 'light' }}>
                    <Mark
                      text={productName}
                      term={remainingTerm}
                      originalTerm={searchTerm}
                      render={match => (
                        <span sx={{ fontWeight: 'body' }}>{match}</span>
                      )}
                    />
                  </Text>
                )}
              />
            )}
          />
        </Box>
      </Flex>
    </SearchResultLink>
  );
}

const QUERY = graphql`
  fragment SearchResult_Product on Product {
    slug
    shortName
    ...ProductImageSmaller_list
    exhibitor {
      name
      slug
    }
    producer {
      name
    }
  }

  fragment SearchResult_Brand on AbstractBrand {
    slug
    name
    countryName: country(full: true)
    region
    city
    ...BrandLogoSmaller_list
  }

  query SiteSearch {
    allExhibitor {
      nodes {
        locale
        slug
        ...SearchResult_Brand
        ...Search_Exhibitor
      }
    }
    allProduct {
      nodes {
        locale
        ...SearchResult_Product
        ...Search_Product
      }
    }
  }
`;
